import { logger } from './logger'
import type { HealthProviderListing } from '../types/listing'

// Types for Zocdoc API responses
interface ZocdocProviderResponse {
  id: string
  name: string
  specialty: string
  bio?: string
  consultation_fee?: number
  address?: {
    street: string
    city: string
    state: string
    zip_code: string
  }
  profile_photo?: string
  phone?: string
  npi?: string
  languages?: string[]
  accepts_new_patients?: boolean
  insurance_accepted?: string[]
  next_available_date?: string
  rating?: number
  review_count?: number
  provider_location_id?: string
  visit_reason_ids?: string[]
}

interface ZocdocApiResponse {
  providers?: ZocdocProviderResponse[]
}

// Configuration
const ZOCDOC_API_BASE = import.meta.env.VITE_ZOCDOC_API_BASE || 'https://api.zocdoc.com'
const ZOCDOC_API_KEY = import.meta.env.VITE_ZOCDOC_API_KEY
const ZOCDOC_DIRECTORY_ID = import.meta.env.VITE_ZOCDOC_DIRECTORY_ID

// Mock data for development/demo
const MOCK_PROVIDERS: HealthProviderListing[] = [
  {
    id: 'zocdoc-1',
    type: 'health',
    title: 'Dra. Maria Silva - Clínica Geral',
    description: 'Médica clínica geral com 15 anos de experiência, especializada em saúde da família. Atende em português e inglês.',
    price: 150,
    priceUnit: 'USD',
    location: '123 Medical Center Dr, Orlando, FL 32801',
    city: 'Orlando',
    state: 'FL',
    images: ['https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=800'],
    userId: 'zocdoc-system',
    userName: 'Zocdoc',
    userAvatar: 'https://logo.clearbit.com/zocdoc.com',
    contactPhone: '+1 (407) 555-1234',
    contactWhatsApp: '+1 (407) 555-1234',
    status: 'active',
    approvalStatus: 'approved',
    isPaid: false,
    featuredLevel: 'none',
    mainCategory: 'saude',
    subCategory: 'clinica-geral',
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-03-10'),
    views: 1245,
    likes: 89,
    specialty: 'Clínica Geral',
    providerName: 'Dra. Maria Silva',
    npi: '1234567890',
    languages: ['Português', 'Inglês', 'Espanhol'],
    acceptsNewPatients: true,
    insuranceAccepted: ['Aetna', 'Blue Cross Blue Shield', 'Cigna', 'UnitedHealthcare'],
    nextAvailableDate: '2024-04-20',
    rating: 4.8,
    reviewCount: 124,
    providerLocationId: 'pr_abc123',
    visitReasonIds: ['vr_123']
  },
  {
    id: 'zocdoc-2',
    type: 'health',
    title: 'Dr. João Santos - Cardiologia',
    description: 'Cardiologista especializado em prevenção e tratamento de doenças cardiovasculares. Ex-fellow do Johns Hopkins Hospital.',
    price: 250,
    priceUnit: 'USD',
    location: '456 Heart Center Blvd, Miami, FL 33101',
    city: 'Miami',
    state: 'FL',
    images: ['https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=800'],
    userId: 'zocdoc-system',
    userName: 'Zocdoc',
    userAvatar: 'https://logo.clearbit.com/zocdoc.com',
    contactPhone: '+1 (305) 555-5678',
    contactWhatsApp: '+1 (305) 555-5678',
    status: 'active',
    approvalStatus: 'approved',
    isPaid: true,
    featuredLevel: 'premium',
    mainCategory: 'saude',
    subCategory: 'cardiologia',
    createdAt: new Date('2024-02-20'),
    updatedAt: new Date('2024-03-12'),
    views: 890,
    likes: 45,
    specialty: 'Cardiologia',
    providerName: 'Dr. João Santos',
    npi: '0987654321',
    languages: ['Português', 'Inglês'],
    acceptsNewPatients: true,
    insuranceAccepted: ['Aetna', 'Humana', 'Medicare', 'UnitedHealthcare'],
    nextAvailableDate: '2024-04-18',
    rating: 4.9,
    reviewCount: 89,
    providerLocationId: 'pr_def456',
    visitReasonIds: ['vr_456']
  },
  {
    id: 'zocdoc-3',
    type: 'health',
    title: 'Dra. Ana Oliveira - Pediatria',
    description: 'Pediatra dedicada ao cuidado infantil, com enfoque em desenvolvimento saudável e prevenção. Atendimento humanizado.',
    price: 180,
    priceUnit: 'USD',
    location: '789 Children\'s Way, Houston, TX 77001',
    city: 'Houston',
    state: 'TX',
    images: ['https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800'],
    userId: 'zocdoc-system',
    userName: 'Zocdoc',
    userAvatar: 'https://logo.clearbit.com/zocdoc.com',
    contactPhone: '+1 (713) 555-9012',
    contactWhatsApp: '+1 (713) 555-9012',
    status: 'active',
    approvalStatus: 'approved',
    isPaid: false,
    featuredLevel: 'none',
    mainCategory: 'saude',
    subCategory: 'pediatria',
    createdAt: new Date('2024-03-01'),
    updatedAt: new Date('2024-03-15'),
    views: 543,
    likes: 32,
    specialty: 'Pediatria',
    providerName: 'Dra. Ana Oliveira',
    npi: '1122334455',
    languages: ['Português', 'Inglês', 'Espanhol'],
    acceptsNewPatients: true,
    insuranceAccepted: ['Blue Cross Blue Shield', 'Cigna', 'Medicaid', 'Aetna'],
    nextAvailableDate: '2024-04-22',
    rating: 4.7,
    reviewCount: 67,
    providerLocationId: 'pr_ghi789',
    visitReasonIds: ['vr_789']
  },
  {
    id: 'zocdoc-4',
    type: 'health',
    title: 'Dr. Carlos Mendes - Ortopedia',
    description: 'Ortopedista especializado em cirurgia do joelho e ombro. Tratamento de lesões esportivas e condições musculoesqueléticas.',
    price: 300,
    priceUnit: 'USD',
    location: '321 Bone & Joint Ave, New York, NY 10001',
    city: 'New York',
    state: 'NY',
    images: ['https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?w=800'],
    userId: 'zocdoc-system',
    userName: 'Zocdoc',
    userAvatar: 'https://logo.clearbit.com/zocdoc.com',
    contactPhone: '+1 (212) 555-3456',
    contactWhatsApp: '+1 (212) 555-3456',
    status: 'active',
    approvalStatus: 'approved',
    isPaid: true,
    featuredLevel: 'standard',
    mainCategory: 'saude',
    subCategory: 'ortopedia',
    createdAt: new Date('2024-02-28'),
    updatedAt: new Date('2024-03-14'),
    views: 421,
    likes: 28,
    specialty: 'Ortopedia',
    providerName: 'Dr. Carlos Mendes',
    npi: '5566778899',
    languages: ['Português', 'Inglês'],
    acceptsNewPatients: false,
    insuranceAccepted: ['UnitedHealthcare', 'Aetna', 'Cigna', 'Medicare'],
    nextAvailableDate: '2024-05-10',
    rating: 4.6,
    reviewCount: 42,
    providerLocationId: 'pr_jkl012',
    visitReasonIds: ['vr_012']
  }
]

// Check if we have API credentials
const hasApiCredentials = ZOCDOC_API_KEY && ZOCDOC_DIRECTORY_ID

// Helper function to search providers via Zocdoc API
const searchZocdocProviders = async (filters: {
  location?: string
  specialty?: string
  insurance?: string
  acceptsNewPatients?: boolean
}): Promise<HealthProviderListing[]> => {
  if (!hasApiCredentials) {
    logger.log('Zocdoc API credentials not configured, using mock data')
    // Filter mock data based on filters
    let results = MOCK_PROVIDERS
    if (filters.location) {
      const locationLower = filters.location.toLowerCase()
      results = results.filter(p => 
        p.location.toLowerCase().includes(locationLower) || 
        p.city.toLowerCase().includes(locationLower) ||
        p.state.toLowerCase().includes(locationLower)
      )
    }
    if (filters.specialty) {
      const specialtyLower = filters.specialty.toLowerCase()
      results = results.filter(p => p.specialty.toLowerCase().includes(specialtyLower))
    }
    if (filters.insurance) {
      const insuranceLower = filters.insurance.toLowerCase()
      results = results.filter(p => 
        p.insuranceAccepted.some(ins => ins.toLowerCase().includes(insuranceLower))
      )
    }
    if (filters.acceptsNewPatients !== undefined) {
      results = results.filter(p => p.acceptsNewPatients === filters.acceptsNewPatients)
    }
    return results
  }

  // Real API implementation would go here
  try {
    logger.log('Searching Zocdoc providers with filters:', filters)
    
    // Build query parameters
    const params = new URLSearchParams()
    if (filters.location) params.append('zip_code', filters.location)
    if (filters.specialty) params.append('specialty', filters.specialty)
    
    const response = await fetch(
      `${ZOCDOC_API_BASE}/v1/directories/${ZOCDOC_DIRECTORY_ID}/providers?${params}`,
      {
        headers: {
          'Authorization': `Bearer ${ZOCDOC_API_KEY}`,
          'Content-Type': 'application/json'
        }
      }
    )
    
    if (!response.ok) {
      throw new Error(`Zocdoc API error: ${response.status}`)
    }
    
    const data = await response.json() as ZocdocApiResponse
    
    // Transform Zocdoc API response to HealthProviderListing format
    const providers: HealthProviderListing[] = data.providers?.map((provider: ZocdocProviderResponse) => ({
      id: `zocdoc-${provider.id}`,
      type: 'health' as const,
      title: `${provider.name} - ${provider.specialty}`,
      description: provider.bio || `Médico especializado em ${provider.specialty}`,
      price: provider.consultation_fee || 150,
      priceUnit: 'USD',
      location: `${provider.address?.street}, ${provider.address?.city}, ${provider.address?.state} ${provider.address?.zip_code}`,
      city: provider.address?.city || '',
      state: provider.address?.state || '',
      images: provider.profile_photo ? [provider.profile_photo] : ['https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=800'],
      userId: 'zocdoc-system',
      userName: 'Zocdoc',
      userAvatar: 'https://logo.clearbit.com/zocdoc.com',
      contactPhone: provider.phone || '',
      contactWhatsApp: provider.phone || '',
      status: 'active',
      approvalStatus: 'approved',
      isPaid: false,
      featuredLevel: 'none',
      mainCategory: 'saude',
      subCategory: provider.specialty.toLowerCase().replace(/\s+/g, '-'),
      createdAt: new Date(),
      updatedAt: new Date(),
      views: 0,
      likes: 0,
      specialty: provider.specialty,
      providerName: provider.name,
      npi: provider.npi,
      languages: provider.languages || ['Inglês'],
      acceptsNewPatients: provider.accepts_new_patients || true,
      insuranceAccepted: provider.insurance_accepted || [],
      nextAvailableDate: provider.next_available_date,
      rating: provider.rating,
      reviewCount: provider.review_count,
      providerLocationId: provider.provider_location_id,
      visitReasonIds: provider.visit_reason_ids
    })) || []
    
    return providers
  } catch (error) {
    logger.error('Error fetching Zocdoc providers:', error)
    // Fallback to mock data on error
    return MOCK_PROVIDERS
  }
}

// Service functions
export const zocdocService = {
  async searchProviders(filters: {
    location?: string
    specialty?: string
    insurance?: string
    acceptsNewPatients?: boolean
  } = {}): Promise<HealthProviderListing[]> {
    return searchZocdocProviders(filters)
  },

  async getFeaturedProviders(): Promise<HealthProviderListing[]> {
    // Return providers with featuredLevel > 'none'
    const allProviders = await searchZocdocProviders({})
    return allProviders.filter(p => p.featuredLevel !== 'none')
  },

  async getProviderById(id: string): Promise<HealthProviderListing | null> {
    const allProviders = await searchZocdocProviders({})
    return allProviders.find(p => p.id === id) || null
  }
}